import Phaser from 'phaser';
import type { GameTheme } from '../themes/types';
import { darkenColor } from '../utils/colors';
import { drawRoundedRect } from '../utils/graphics';
import { animateFadeIn, animateFadeOut, animateScaleIn } from '../utils/animations';

export interface ModalConfig {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  theme: GameTheme;
  title?: string;
  overlayAlpha?: number;
  borderRadius?: number;
  borderWidth?: number;
  depth?: number;
}

/**
 * Modal - A reusable base modal component
 *
 * Provides:
 * - Semi-transparent overlay
 * - Rounded panel with border
 * - Title text
 * - Show/hide animations
 * - Content container for child components
 *
 * Extend or compose this for specific modal types (pause, scoreboard, settings, etc.)
 */
export class Modal extends Phaser.GameObjects.Container {
  protected overlay!: Phaser.GameObjects.Rectangle;
  protected panel!: Phaser.GameObjects.Graphics;
  protected titleText!: Phaser.GameObjects.Text;
  protected contentContainer!: Phaser.GameObjects.Container;
  protected modalConfig: Required<ModalConfig>;

  constructor(scene: Phaser.Scene, config: ModalConfig) {
    const { width, height } = scene.cameras.main;

    super(scene, config.x ?? width / 2, config.y ?? height / 2);

    // Set defaults
    this.modalConfig = {
      x: config.x ?? width / 2,
      y: config.y ?? height / 2,
      width: config.width ?? 400,
      height: config.height ?? 300,
      theme: config.theme,
      title: config.title ?? '',
      overlayAlpha: config.overlayAlpha ?? 0.7,
      borderRadius: config.borderRadius ?? 24,
      borderWidth: config.borderWidth ?? 4,
      depth: config.depth ?? 1000,
    };

    this.createModal();

    // Start hidden
    this.setVisible(false);
    this.setDepth(this.modalConfig.depth);

    scene.add.existing(this);
  }

  protected createModal(): void {
    const { width, height } = this.scene.cameras.main;
    const {
      width: panelWidth,
      height: panelHeight,
      theme,
      title,
      overlayAlpha,
      borderRadius,
      borderWidth,
    } = this.modalConfig;

    // Semi-transparent overlay covering entire screen
    this.overlay = this.scene.add.rectangle(0, 0, width * 2, height * 2, 0x000000, overlayAlpha);
    this.add(this.overlay);

    // Panel background
    this.panel = this.scene.add.graphics();
    drawRoundedRect(
      this.panel,
      -panelWidth / 2,
      -panelHeight / 2,
      panelWidth,
      panelHeight,
      borderRadius,
      theme.panelDark,
      darkenColor(theme.panelDark, 30),
      borderWidth
    );
    this.add(this.panel);

    // Title text (if provided)
    if (title) {
      this.titleText = this.scene.add.text(0, -panelHeight / 2 + 60, title, {
        fontFamily: 'Arial',
        fontSize: '48px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 6,
        fontStyle: 'bold',
      });
      this.titleText.setOrigin(0.5);
      this.add(this.titleText);
    }

    // Content container for child components
    this.contentContainer = this.scene.add.container(0, 0);
    this.add(this.contentContainer);
  }

  /**
   * Add a game object to the modal's content container
   */
  public addContent(child: Phaser.GameObjects.GameObject): this {
    this.contentContainer.add(child);
    return this;
  }

  /**
   * Remove a game object from the modal's content container
   */
  public removeContent(child: Phaser.GameObjects.GameObject): this {
    this.contentContainer.remove(child);
    return this;
  }

  /**
   * Clear all content from the modal
   */
  public clearContent(): this {
    this.contentContainer.removeAll(true);
    return this;
  }

  /**
   * Get the content container for direct manipulation
   */
  public getContentContainer(): Phaser.GameObjects.Container {
    return this.contentContainer;
  }

  /**
   * Get the panel dimensions for positioning child elements
   */
  public getPanelDimensions(): { width: number; height: number } {
    return {
      width: this.modalConfig.width,
      height: this.modalConfig.height,
    };
  }

  /**
   * Show the modal with animation
   */
  public show(): void {
    if (this.visible) return;

    this.setVisible(true);
    this.setAlpha(0);
    this.setScale(0.8);

    // Animate in
    animateFadeIn(this.scene, this, 200);
    animateScaleIn(this.scene, this, 200);
  }

  /**
   * Hide the modal with animation
   */
  public hide(): void {
    if (!this.visible) return;

    animateFadeOut(this.scene, this, 200, () => {
      this.setVisible(false);
      this.setScale(1);
    });
  }

  /**
   * Check if modal is currently visible
   */
  public isShowing(): boolean {
    return this.visible;
  }

  /**
   * Update the title text
   */
  public setTitle(title: string): void {
    if (this.titleText) {
      this.titleText.setText(title);
    }
  }

  /**
   * Get the theme
   */
  public getTheme(): GameTheme {
    return this.modalConfig.theme;
  }

  /**
   * Clean up and destroy
   */
  public destroy(): void {
    this.overlay?.destroy();
    this.panel?.destroy();
    this.titleText?.destroy();
    this.contentContainer?.destroy(true);

    super.destroy();
  }
}
